import { ColumnDef, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { StatusSort, useExpandedSortState } from "../../state/sorting.state";

import APIService from '../../../../services/api.service';
import { ExpandedTableBody } from './ExpandedTableBody';
import { ExpandedTableHeader } from './ExpandedTableHeader';
import { FaCircle } from 'react-icons/fa';
import { IndeterminateCheckbox } from '../../../../shared/components/IndeterminateCheckbox';
import { Item } from '../../types/item.types';
import SkapaTable from '@ingka/table';
import { SubItem } from '../../types/sub-item.types';
import shallow from 'zustand/shallow';
import styled from '@emotion/styled';
import { filterEmissionColumnsByRole, tableColumns } from '../table/ColumnDefs';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useAuth } from '../../../auth/state/auth.state';
import { useColumnOrderState } from '../../state/column-order.state';
import { useHiddenColumnState } from '../../state/column-hiding.state';
import { useSelectionOnRow } from '../../state/selection.state';
import { applyOrder } from '../../../../shared/utils/order';

type ExpandedTableProps = {
   parentIndex: number;
   parentItem: Item;
   subItems?: SubItem[];
};

declare module '@tanstack/table-core' {
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   interface TableMeta<TData = unknown> {
      parentPA: string;
   }
}

export const ExpandedTable = ({ parentIndex, subItems, parentItem }: ExpandedTableProps) => {
   const [hiddenColumns] = useHiddenColumnState((state: any) => [state.hiddenColumns], shallow);
   const { canManageEmissionFactors } = useAuth();
   const { sorting, setSorting, statusSort } = useExpandedSortState();
   const { rowSelection, setRowSelection } = useSelectionOnRow(parentIndex);
   const [viewedItems, setViewedItems] = useState<Set<number>>(new Set());
   const expandedOrder = useColumnOrderState(s => s.orders.expanded);
   const orderedExpanded = useMemo(
      () => applyOrder(filterEmissionColumnsByRole(tableColumns.expanded, canManageEmissionFactors), expandedOrder, c => c.accessorKey),
      [expandedOrder, canManageEmissionFactors]
   );

   const data = subItems || [];

   const columnVisibility = useMemo(() => ({ ...hiddenColumns }), [hiddenColumns]);

   const handleRowView = async (item: SubItem) => {
      if (item.newFromPia && !viewedItems.has(item.id)) {
         try {
            await APIService.updateNewFromPiaStatus(item);
            setViewedItems(prev => new Set(prev).add(item.id));
         } catch (error) {
            console.error('Failed to update newFromPia status:', error);
         }
      } else {
         setViewedItems(prev => new Set(prev).add(item.id));
      }
   };

   const columns = useMemo<ColumnDef<SubItem>[]>(
      () => [
         {
            id: 'select',
            accessorKey: '_',
            header: () => <></>,
            cell: ({ row }) => (
               <StyledCheckboxDiv>
                  <IndeterminateCheckbox
                     checked={row.getIsSelected()}
                     indeterminate={row.getIsSomeSelected()}
                     onChange={row.getToggleSelectedHandler()}
                     disabled={parentItem.deleted}
                  />
                  {!parentItem.deleted && row.original.newFromPia && !viewedItems.has(row.original.id) && (
                     <GreenCircle />
                  )}
               </StyledCheckboxDiv>
            ),
            meta: {
               width: '4rem',
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
               switch (statusSort) {
                  case StatusSort.active:
                     return rowA.original.active === rowB.original.active ? 0 : rowA.original.active ? -1 : 1;
                  case StatusSort.locked:
                     return rowA.original.locked === rowB.original.locked ? 0 : rowA.original.locked ? -1 : 1;
                  default:
                     return 0;
               }
            },
         },
         ...orderedExpanded,
      ],
      [parentItem.deleted, statusSort, viewedItems, orderedExpanded]
   );

   const instance = useReactTable({
      data,
      columns,
      state: {
         rowSelection: rowSelection as any,
         columnVisibility,
         sorting: sorting,
      },
      meta: {
         parentPA: parentItem.pa
      },
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   return (
      <StyledSkapaTable fullWidth={true} inset={true} style={parentItem.deleted ? { opacity: 0.5 } : undefined}>
         <ExpandedTableHeader
            headerGroups={instance.getHeaderGroups()}
            subItems={data}
            parentIndex={parentIndex}
         />
         <ExpandedTableBody
            instance={instance}
            parentIndex={parentIndex}
            parentItem={parentItem}
            onViewItem={handleRowView}
            viewedItems={viewedItems}
         />
      </StyledSkapaTable>
   );
};

const StyledSkapaTable = styled(SkapaTable)`
  table {
    table-layout: auto;
    padding-left: 2.75rem;
  }
  max-height: 40vh;
  overflow-y: auto;
  scrollbar-width: none;

  &:hover {
    scrollbar-width: thin;
  }

  &::-webkit-scrollbar {
    width: 0;
  }

  &:hover::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.border.strong};
    border-radius: ${tokens.radius.md};
  }

  thead {
    position: sticky;
    top: 0;
    background: ${theme.colors.bg.primary};
    z-index: ${tokens.zIndex.base};
  }
`;

const StyledCheckboxDiv = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${tokens.space.xl};
  gap: ${tokens.space.sm};
`;

const GreenCircle = styled(FaCircle)`
  color: ${theme.colors.state.success};
  font-size: ${tokens.font.size.sm};
`;
